
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Rocket, Github, BookOpen } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { StartupGrid } from "@/components/startup/StartupGrid";
import { Link } from "react-router-dom";

export const TrendingTabs = () => {
  const { data: pinnedStartups } = useQuery({
    queryKey: ['pinned-startups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startups')
        .select(`
          *,
          profiles(is_pro),
          pinned_startups(id)
        `)
        .not('pinned_startups', 'is', null);

      if (error) {
        console.error('Error fetching pinned startups:', error);
        return [];
      }

      return data || [];
    }
  });

  const { data: pinnedArticles } = useQuery({
    queryKey: ['pinned-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          profiles(username, avatar_url, is_pro)
        `)
        .eq('is_pinned', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pinned articles:', error);
        return [];
      }

      return data || [];
    }
  });

  return (
    <div className="w-full bg-gradient-to-r from-[#00F0FF]/5 to-[#0EA5E9]/5 border-t border-[#00F0FF]/20 backdrop-blur-xl py-4 mt-auto">
      <Tabs defaultValue="startups" className="max-w-7xl mx-auto px-4">
        <TabsList className="w-full justify-center bg-black/50 border border-[#00F0FF]/20">
          <TabsTrigger value="startups" className="flex items-center gap-2">
            <Rocket className="w-4 h-4" />
            <span className="hidden sm:inline">Trending Startups</span>
          </TabsTrigger>
          <TabsTrigger value="opensource" className="flex items-center gap-2">
            <Github className="w-4 h-4" />
            <span className="hidden sm:inline">Trending Open-Source</span>
          </TabsTrigger>
          <TabsTrigger value="articles" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Trending Articles</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="startups" className="mt-4">
          <div className="p-4 rounded-xl bg-black/50 border border-[#00F0FF]/20 hover:border-[#00F0FF]/40 hover:bg-gradient-to-r hover:from-[#00F0FF]/10 hover:to-[#0EA5E9]/10 transition-all duration-300">
            {pinnedStartups && pinnedStartups.length > 0 ? (
              <StartupGrid
                startups={pinnedStartups}
                isAdmin={false}
                onPin={() => {}}
                onUnpin={() => {}}
              />
            ) : (
              <p className="text-gray-400">No trending startups at the moment...</p>
            )}
          </div>
        </TabsContent>
        <TabsContent value="opensource" className="mt-4">
          <div className="p-4 rounded-xl bg-black/50 border border-[#00F0FF]/20 hover:border-[#00F0FF]/40 hover:bg-gradient-to-r hover:from-[#00F0FF]/10 hover:to-[#0EA5E9]/10 transition-all duration-300">
            <p className="text-gray-400">Popular open-source projects will appear here...</p>
          </div>
        </TabsContent>
        <TabsContent value="articles" className="mt-4">
          <div className="p-4 rounded-xl bg-black/50 border border-[#00F0FF]/20 hover:border-[#00F0FF]/40 hover:bg-gradient-to-r hover:from-[#00F0FF]/10 hover:to-[#0EA5E9]/10 transition-all duration-300">
            {pinnedArticles && pinnedArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pinnedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/library/articles/${article.id}`}
                    className="group block"
                  >
                    <div className="p-4 rounded-lg bg-black/50 border border-[#00F0FF]/20 hover:border-[#00F0FF]/40 hover:bg-gradient-to-r hover:from-[#00F0FF]/10 hover:to-[#0EA5E9]/10 transition-all duration-300">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-[#00F0FF] group-hover:text-[#00F0FF]/80 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                        {article.short_description}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{article.profiles?.username}</span>
                        {article.profiles?.is_pro && (
                          <span className="px-1.5 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF] text-xs">
                            PRO
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No trending articles at the moment...</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
